import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Lock,
  Loader2,
  BookOpen,
  EyeOff,
  RotateCcw,
  Search,
  Eye,
  ExternalLink,
} from "lucide-react";
import { chartOfAccountsApi } from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import AccountLedgerModal from "../accounting/AccountLedgerModal";

interface Account {
  _id: string;
  code: string;
  name: string;
  type: "asset" | "liability" | "equity" | "revenue" | "expense";
  subtype: string | null;
  currency: string;
  isSystem: boolean;
  active: boolean;
}

const typeConfig: Record<string, { label: string; bg: string; text: string }> = {
  asset: { label: "Assets", bg: "bg-blue-50", text: "text-blue-700" },
  liability: { label: "Liabilities", bg: "bg-amber-50", text: "text-amber-700" },
  equity: { label: "Equity", bg: "bg-purple-50", text: "text-purple-700" },
  revenue: { label: "Revenue", bg: "bg-emerald-50", text: "text-emerald-700" },
  expense: { label: "Expenses", bg: "bg-red-50", text: "text-red-700" },
};

const typeOrder = ["asset", "liability", "equity", "revenue", "expense"];

/**
 * Chart of Accounts content  -  embeddable without outer page wrapper.
 * Used by AccountingPage as a sub-tab.
 */
function formatCents(cents: number): string {
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return cents < 0 ? `-${formatted}` : formatted;
}

export const ChartOfAccountsContent: React.FC = () => {
  const [grouped, setGrouped] = useState<Record<string, Account[]>>({});
  const [balanceMap, setBalanceMap] = useState<Map<string, number>>(new Map());
  const [typeTotals, setTypeTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set(typeOrder));
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);

  // Search & filter
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // Account ledger drill-down
  const [ledgerAccount, setLedgerAccount] = useState<Account | null>(null);

  // Form
  const [form, setForm] = useState({
    code: "",
    name: "",
    type: "asset" as string,
    subtype: "",
    currency: "CAD",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, balRes] = await Promise.all([
        chartOfAccountsApi.list(),
        chartOfAccountsApi.balances(),
      ]);
      setGrouped(listRes.data?.data || {});

      // Build balance lookup map from balances response
      const bMap = new Map<string, number>();
      const balData = balRes.data?.data || [];
      for (const row of balData) {
        // balanceCents = debit - credit (raw)
        // For credit-normal accounts (liability, equity, revenue) negate so positive = expected direction
        const isCreditNormal = row.type === "liability" || row.type === "equity" || row.type === "revenue";
        bMap.set(row.accountId, isCreditNormal ? -(row.balanceCents || 0) : (row.balanceCents || 0));
      }
      setBalanceMap(bMap);

      // Type totals (using normal-balance convention)
      const totals: Record<string, number> = {};
      for (const row of balData) {
        const isCreditNormal = row.type === "liability" || row.type === "equity" || row.type === "revenue";
        const normalBal = isCreditNormal ? -(row.balanceCents || 0) : (row.balanceCents || 0);
        totals[row.type] = (totals[row.type] || 0) + normalBal;
      }
      setTypeTotals(totals);
    } catch (err) {
      console.error("Failed to load chart of accounts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const toggleType = (type: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const openCreate = () => {
    setEditingAccount(null);
    setForm({ code: "", name: "", type: "asset", subtype: "", currency: "CAD" });
    setSaveError("");
    setShowCreateModal(true);
  };

  const openEdit = (account: Account) => {
    if (account.isSystem) return;
    setEditingAccount(account);
    setForm({
      code: account.code,
      name: account.name,
      type: account.type,
      subtype: account.subtype || "",
      currency: account.currency,
    });
    setSaveError("");
    setShowCreateModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      if (editingAccount) {
        await chartOfAccountsApi.update(editingAccount._id, {
          code: form.code,
          name: form.name,
          subtype: form.subtype || null,
          currency: form.currency,
        });
      } else {
        await chartOfAccountsApi.create({
          code: form.code,
          name: form.name,
          type: form.type,
          subtype: form.subtype || null,
          currency: form.currency,
        });
      }
      setShowCreateModal(false);
      fetchAccounts();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Deactivate this account? It will no longer appear in selections.")) return;
    try {
      await chartOfAccountsApi.deactivate(id);
      fetchAccounts();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to deactivate");
    }
  };

  const handleReactivate = async (id: string) => {
    if (!window.confirm("Reactivate this account?")) return;
    try {
      await chartOfAccountsApi.reactivate(id);
      fetchAccounts();
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error || "Failed to reactivate");
    }
  };

  const totalAccounts = typeOrder.reduce(
    (sum, type) => sum + (grouped[type]?.length || 0),
    0
  );

  // Filter accounts by search query and active status
  const filteredGrouped = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const result: Record<string, Account[]> = {};
    for (const type of typeOrder) {
      const accounts = grouped[type] || [];
      result[type] = accounts.filter((a) => {
        if (!showInactive && !a.active) return false;
        if (query && !a.code.toLowerCase().includes(query) && !a.name.toLowerCase().includes(query)) return false;
        return true;
      });
    }
    return result;
  }, [grouped, searchQuery, showInactive]);

  const filteredTotal = typeOrder.reduce(
    (sum, type) => sum + (filteredGrouped[type]?.length || 0),
    0
  );

  const inactiveCount = typeOrder.reduce(
    (sum, type) => sum + ((grouped[type] || []).filter((a) => !a.active).length),
    0
  );

  return (
    <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Chart of Accounts</h2>
          <p className="text-sm text-slate-500 mt-1">
            {totalAccounts} account{totalAccounts !== 1 ? "s" : ""} configured
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-xl bg-brand hover:bg-brand-hover transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Account
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      {/* Error Banner */}
      {errorMessage && (
        <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          <Lock className="h-4 w-4 shrink-0" />
          <span className="flex-1">{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="ml-auto text-red-400 hover:text-red-600 transition-colors">&times;</button>
        </div>
      )}

      {totalAccounts > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by account code or name..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
              showInactive
                ? "bg-slate-800 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {showInactive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showInactive ? "Showing Inactive" : "Show Inactive"}
            {inactiveCount > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                showInactive ? "bg-white/20" : "bg-slate-100"
              }`}>
                {inactiveCount}
              </span>
            )}
          </button>
        </div>
      )}

        {/* Tree View */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : totalAccounts === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No accounts yet</p>
            <p className="text-sm text-slate-400 mt-1">Default accounts are created automatically on your next login, or add one manually.</p>
            <button
              onClick={openCreate}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm bg-brand hover:bg-brand-hover transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Account
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {typeOrder.map((type) => {
              const config = typeConfig[type];
              const accounts = filteredGrouped[type] || [];
              const isExpanded = expandedTypes.has(type);
              if (accounts.length === 0 && searchQuery) return null;

              return (
                <div key={type} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Type Header */}
                  <button
                    onClick={() => toggleType(type)}
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
                        {config.label}
                      </span>
                      <span className="text-sm text-slate-500">{accounts.length} account{accounts.length !== 1 ? "s" : ""}</span>
                    </div>
                    {typeTotals[type] != null && (
                      <span className={`font-mono text-sm font-semibold ${typeTotals[type] < 0 ? "text-red-600" : "text-slate-700"}`}>
                        {formatCents(typeTotals[type])}
                      </span>
                    )}
                  </button>

                  {/* Account List */}
                  <AnimatePresence>
                    {isExpanded && accounts.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="border-t border-slate-200">
                          {accounts.map((account, i) => (
                            <div
                              key={account._id}
                              className={`flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors ${
                                !account.active ? "opacity-50" : ""
                              } ${i < accounts.length - 1 ? "border-b border-slate-100" : ""}`}
                            >
                              <div className="flex items-center gap-4 min-w-0 flex-1">
                                <span className="font-mono text-sm text-slate-500 w-16 shrink-0">{account.code}</span>
                                <button
                                  onClick={() => setLedgerAccount(account)}
                                  className={`text-sm font-medium hover:underline text-left truncate ${account.active ? "text-brand hover:text-brand-hover" : "text-slate-400 hover:text-slate-500"}`}
                                  title="View account ledger"
                                >
                                  {account.name}
                                </button>
                                {account.isSystem && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500 shrink-0">
                                    <Lock className="h-3 w-3" /> System
                                  </span>
                                )}
                                {!account.active && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600 shrink-0">
                                    <EyeOff className="h-3 w-3" /> Inactive
                                  </span>
                                )}
                              </div>
                              {/* Balance */}
                              <span className={`font-mono text-sm shrink-0 w-28 text-right ${
                                (balanceMap.get(account._id) || 0) < 0 ? "text-red-600" : (balanceMap.get(account._id) || 0) > 0 ? "text-slate-700" : "text-slate-400"
                              }`}>
                                {balanceMap.has(account._id) ? formatCents(balanceMap.get(account._id)!) : "-"}
                              </span>
                              <div className="flex gap-1.5 shrink-0 ml-3">
                                <button
                                  onClick={() => setLedgerAccount(account)}
                                  className="text-xs font-medium px-3 py-1.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1"
                                  title="View ledger"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Ledger
                                </button>
                                {!account.isSystem && account.active && (
                                  <>
                                    <button
                                      onClick={() => openEdit(account)}
                                      className="text-xs font-medium px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeactivate(account._id); }}
                                      className="text-xs text-slate-400 hover:text-red-600 px-1.5 py-1.5 transition-colors"
                                      title="Deactivate account"
                                    >
                                      <EyeOff className="h-3.5 w-3.5" />
                                    </button>
                                  </>
                                )}
                                {!account.isSystem && !account.active && (
                                  <button
                                    onClick={() => handleReactivate(account._id)}
                                    className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-xl text-emerald-700 hover:bg-emerald-50 transition-colors"
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                    Reactivate
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            {filteredTotal === 0 && searchQuery && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium text-slate-600">No accounts match "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-2 text-xs text-brand hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        <PremiumModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title={editingAccount ? "Edit Account" : "New Account"}
          icon={BookOpen}
          size="md"
          footer={
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                type="submit" form="chartAccountForm" disabled={saving || !form.code || !form.name}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editingAccount ? "Save Changes" : "Create Account"}
              </button>
            </div>
          }
        >
          {saveError && <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{saveError}</div>}

          <form id="chartAccountForm" onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
                <input type="text" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 font-mono" placeholder="1000" />
              </div>
              {!editingAccount && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                  <SearchableSelect
                    options={[
                      { value: "asset", label: "Asset" },
                      { value: "liability", label: "Liability" },
                      { value: "equity", label: "Equity" },
                      { value: "revenue", label: "Revenue" },
                      { value: "expense", label: "Expense" },
                    ]}
                    value={form.type}
                    onChange={(val) => setForm({ ...form, type: val || "asset" })}
                    placeholder="Select type"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Account Name *</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="e.g. Trust Bank Account" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Subtype</label>
              <input type="text" value={form.subtype} onChange={(e) => setForm({ ...form, subtype: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600" placeholder="e.g. trust_cash" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
              <SearchableSelect
                options={[
                  { value: "CAD", label: "CAD" },
                  { value: "USD", label: "USD" },
                ]}
                value={form.currency}
                onChange={(val) => setForm({ ...form, currency: val || "CAD" })}
                placeholder="Select currency"
              />
            </div>
          </form>
      </PremiumModal>

      {/* Account Ledger Drill-Down */}
      <AccountLedgerModal
        open={!!ledgerAccount}
        onClose={() => setLedgerAccount(null)}
        accountId={ledgerAccount?._id || ""}
        accountCode={ledgerAccount?.code || ""}
        accountName={ledgerAccount?.name || ""}
        accountType={ledgerAccount?.type}
      />
    </>
  );
};

/** Standalone page wrapper  -  kept for backward compat / direct route */
const ChartOfAccountsPage: React.FC = () => (
  <div className="min-h-screen" style={{ backgroundColor: "#F4F6F8" }}>
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      <ChartOfAccountsContent />
    </div>
  </div>
);

export default ChartOfAccountsPage;
